import { useEffect, useState } from 'react'
import { BUILD, type BuildInfo } from './build'
import { isNewerBuild } from './buildCompare'

/** Often enough to catch a deploy within the hour, rarely enough to be free. */
const EVERY = 10 * 60 * 1000

async function fetchLatest(): Promise<BuildInfo | null> {
  try {
    // `no-store` on the request as well as the response header: a service worker or an
    // intermediate cache answering this from memory would defeat the whole check.
    const res = await fetch('/build-info.json', { cache: 'no-store' })
    if (!res.ok) return null
    return (await res.json()) as BuildInfo
  } catch {
    // Offline, or an error page that is not JSON. Neither means a deploy happened.
    return null
  }
}

/**
 * Checks on mount, whenever the tab is brought back to the front, and every ten minutes.
 *
 * Coming back to the front is the check that matters: the usual shape of this is a tab left
 * open overnight, and somebody returning to it is exactly when they are about to act on
 * whatever it shows.
 */
export function useAppVersion(): { updateAvailable: boolean; latest: BuildInfo | null } {
  const [latest, setLatest] = useState<BuildInfo | null>(null)

  useEffect(() => {
    let live = true
    const check = async () => {
      const next = await fetchLatest()
      if (live && next) setLatest(next)
    }

    void check()
    const timer = setInterval(() => { void check() }, EVERY)
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      live = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [])

  return { updateAvailable: isNewerBuild(BUILD, latest), latest }
}
